/**
 * The "Move" control on a kiosk chore (#583, owner decision d-20261001-42). Two ways to use it, the
 * same moves either way:
 *
 *  - TAP or click (and Enter / Space): opens a short list of exactly the targets this person may
 *    choose. This is the keyboard and screen-reader path.
 *  - DRAG: on a touch screen, press the control for DRAG_HOLD_MS, then slide onto a column. With a
 *    mouse, press and move past DRAG_SLOP_PX. A drop lands only on an element carrying a
 *    `data-drop-target` key that is one of `targets`; anywhere else moves nothing.
 *
 * Why the drag starts on this control and not on the lantern: the lantern's own 600 ms hold lights
 * it (d-20261001-43), and two gestures sharing one press would fight. This control is a separate
 * element with `touch-action: none`, so the browser never scrolls from it; a swipe anywhere else on
 * the board still scrolls. A touch that moves before the long-press ends is dropped, not dragged.
 *
 * It never shows a move as done. `onMove` hands the choice to the host, which asks the server.
 * ponytail: no auto-scroll while dragging near the screen edge; the tap list reaches every target.
 */
import React, { useEffect, useId, useRef, useState } from 'react';

export const DRAG_HOLD_MS = 250;
export const DRAG_SLOP_PX = 8;
const CLICK_SUPPRESS_MS = 500;

function dropKeyAt(x, y, keys) {
  if (typeof document === 'undefined' || typeof document.elementFromPoint !== 'function') return null;
  const hit = document.elementFromPoint(x, y);
  const zone = hit && typeof hit.closest === 'function' ? hit.closest('[data-drop-target]') : null;
  const key = zone ? zone.getAttribute('data-drop-target') : null;
  return key && keys.includes(key) ? key : null;
}

/**
 * @param {object} props
 * @param {string} props.title  the chore's name
 * @param {Array<{key:string, userId:number|null, label:string}>} props.targets
 * @param {boolean} [props.busy]  a move of this chore is in flight
 * @param {(target:{key:string, userId:number|null, label:string}) => void} props.onMove
 * @param {(drag:{active:boolean, overKey:string|null}) => void} [props.onDragChange]
 * @param {() => void} [props.onInteract]  a deliberate interaction began (menu opened, drag started)
 */
export default function ChoreMoveControl({ title, targets, busy = false, onMove, onDragChange, onInteract }) {
  const [open, setOpen] = useState(false);
  const [ghost, setGhost] = useState(null);
  const gesture = useRef(null);
  const suppressClick = useRef(null);
  const menuRef = useRef(null);
  const rootRef = useRef(null);
  const gripRef = useRef(null);
  const menuId = useId();
  const keys = targets.map((t) => t.key);
  // The unmount cleanup below runs once; it must tell the host's CURRENT handler, not the first one.
  const dragChangeRef = useRef(onDragChange);
  dragChangeRef.current = onDragChange;

  useEffect(() => () => {
    clearTimeout(gesture.current?.timer);
    clearTimeout(suppressClick.current);
    // Unmounted mid-drag (a board reload can remount it): the host must not keep the drop targets lit.
    if (gesture.current?.dragging) dragChangeRef.current?.({ active: false, overKey: null });
    gesture.current = null;
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    menuRef.current?.querySelector('button')?.focus();
    // A press anywhere outside this control closes the list, so two lists are never open at once.
    // Focus stays where that press put it.
    function onOutside(event) {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener('pointerdown', onOutside, true);
    return () => document.removeEventListener('pointerdown', onOutside, true);
  }, [open]);

  /** Escape, Cancel or a choice: close the list and put focus back on the Move button. */
  function closeMenu() {
    setOpen(false);
    gripRef.current?.focus();
  }

  function endDrag() {
    const g = gesture.current;
    gesture.current = null;
    if (!g) return;
    clearTimeout(g.timer);
    if (g.dragging) {
      setGhost(null);
      onDragChange?.({ active: false, overKey: null });
    }
  }

  function startDrag() {
    const g = gesture.current;
    if (!g || g.dragging) return;
    g.dragging = true;
    setOpen(false);
    setGhost({ x: g.x, y: g.y });
    onInteract?.();
    onDragChange?.({ active: true, overKey: null });
  }

  function onPointerDown(event) {
    if (busy || event.button > 0 || event.isPrimary === false) return;
    clearTimeout(gesture.current?.timer);
    const g = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, type: event.pointerType, dragging: false, overKey: null, timer: null };
    gesture.current = g;
    if (g.type !== 'mouse') g.timer = setTimeout(startDrag, DRAG_HOLD_MS);
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch { /* not capturable here */ }
  }

  function onPointerMove(event) {
    const g = gesture.current;
    if (!g || event.pointerId !== g.pointerId) return;
    if (!g.dragging) {
      if (Math.hypot(event.clientX - g.x, event.clientY - g.y) <= DRAG_SLOP_PX) return;
      if (g.type !== 'mouse') {
        // Moved before the long-press finished: not a drag.
        endDrag();
        return;
      }
      startDrag();
    }
    event.preventDefault?.();
    setGhost({ x: event.clientX, y: event.clientY });
    const over = dropKeyAt(event.clientX, event.clientY, keys);
    if (over !== g.overKey) {
      g.overKey = over;
      onDragChange?.({ active: true, overKey: over });
    }
  }

  function onPointerUp(event) {
    const g = gesture.current;
    if (!g || event.pointerId !== g.pointerId) return;
    const dropped = g.dragging ? dropKeyAt(event.clientX, event.clientY, keys) : null;
    const wasDrag = g.dragging;
    endDrag();
    if (!wasDrag) return;
    // The lift may also produce a click on this control; it must not open the list.
    clearTimeout(suppressClick.current);
    suppressClick.current = setTimeout(() => { suppressClick.current = null; }, CLICK_SUPPRESS_MS);
    const target = targets.find((t) => t.key === dropped);
    if (target) onMove(target);
  }

  function onClick() {
    if (suppressClick.current) {
      clearTimeout(suppressClick.current);
      suppressClick.current = null;
      return;
    }
    if (busy) return;
    setOpen((was) => {
      if (!was) onInteract?.();
      return !was;
    });
  }

  function choose(target) {
    closeMenu();
    onMove(target);
  }

  return (
    <div ref={rootRef} className="dsp-move" data-part="chore-move">
      <button
        ref={gripRef}
        type="button"
        className="dsp-move-grip"
        aria-label={`Move: ${title}`}
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={busy}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={endDrag}
        onLostPointerCapture={() => { if (gesture.current && !gesture.current.dragging) endDrag(); }}
        onClick={onClick}
        onContextMenu={(event) => event.preventDefault()}
      >
        <span aria-hidden="true" className="dsp-move-dots">⠿</span>
        <span aria-hidden="true">{busy ? 'Moving…' : 'Move'}</span>
      </button>
      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          role="group"
          aria-label={`Where should ${title} go?`}
          className="dsp-move-menu"
          onKeyDown={(event) => { if (event.key === 'Escape') closeMenu(); }}
        >
          {targets.map((target) => (
            <button key={target.key} type="button" onClick={() => choose(target)}>{target.label}</button>
          ))}
          <button type="button" className="dsp-move-cancel" onClick={closeMenu}>Cancel</button>
        </div>
      ) : null}
      {ghost ? (
        <span className="dsp-move-ghost" aria-hidden="true" style={{ left: ghost.x, top: ghost.y }}>{title}</span>
      ) : null}
    </div>
  );
}
